import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	invalidateAccountState,
	invalidateWalletBalances,
} from "./invalidation-helpers";
import { useWallet } from "./use-wallet";

/**
 * Manual "refresh assets" action for the wallet/account UI. Refetches the perp
 * account (+ positions/margin), spot balances, and on-chain wallet USDC — the
 * full asset picture — and resolves once the refetches settle (the invalidation
 * helpers return the `invalidateQueries` promise).
 *
 * Two-layer guard against rapid consecutive clicks: `isRefreshing` blocks
 * re-entry while a refetch is in flight, and a `cooldownMs` window blocks it for
 * a beat afterwards. Not just UX — spamming refetch bursts the shared office IP
 * against Hyperliquid's per-IP REST limit (~1200/min).
 */
export function useRefreshAssets(cooldownMs = 5000) {
	const queryClient = useQueryClient();
	const { address } = useWallet();
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [coolingDown, setCoolingDown] = useState(false);
	const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
		},
		[],
	);

	const disabled = !address || isRefreshing || coolingDown;

	const refresh = useCallback(async () => {
		if (!address || isRefreshing || coolingDown) return;
		setIsRefreshing(true);
		try {
			await Promise.all([
				invalidateAccountState(queryClient, address),
				invalidateWalletBalances(queryClient, address),
			]);
		} finally {
			setIsRefreshing(false);
			setCoolingDown(true);
			cooldownTimer.current = setTimeout(
				() => setCoolingDown(false),
				cooldownMs,
			);
		}
	}, [address, isRefreshing, coolingDown, queryClient, cooldownMs]);

	return { refresh, isRefreshing, disabled };
}
